/**
 * useFunnel — the HOST-side anonymous conversion-funnel emitter (T-PUB-007).
 *
 * The pure renderer (`donation-form/**`) stays byte-unchanged: it emits no funnel
 * events. The funnel is wired 100% host-side from the renderer's EXISTING signals —
 * `DonationFormView` calls `viewed()` once on a published load and `started()` on the
 * first `@step-change`; `useDonationSubmit` calls `submitted()` at the start of a
 * submit attempt and `redirectedToPayment()` immediately before the OPAY redirect.
 *
 * SESSION TOKEN — an ANONYMOUS per-visit correlation token, minted ONCE per page load
 * (`crypto.randomUUID()`) and reused across every event of that visit so the backend can
 * dedup/order the funnel. It lives in MODULE scope (not per-composable-instance, not a
 * cookie, not localStorage, not sessionStorage): `DonationFormView` and `useDonationSubmit`
 * are separate composable instances of the SAME page visit, so they MUST share one token.
 * A fresh page load (new module evaluation) mints a fresh token. The token is opaque and
 * anonymous — it is NEVER the submission ref and carries NO PII (R73).
 *
 * Each helper is fire-and-forget: it delegates to `recordEvent` (which never throws) and is
 * intentionally NOT awaited at the call sites — an analytics beacon must never delay the UI
 * (especially `redirectedToPayment`, fired just before navigating away).
 */
import { recordEvent, recordEventBeacon, } from "@/services/funnelService";
/**
 * The per-visit anonymous session token, minted lazily ONCE per page load and shared by
 * every `useFunnel` consumer. Module-scoped on purpose: a single load = a single token.
 */
let visitSessionToken = null;
function sessionToken() {
    if (visitSessionToken === null) {
        visitSessionToken = crypto.randomUUID();
    }
    return visitSessionToken;
}
/**
 * The BARE referrer host of the embedding/origin page (no path, no query, no PII), or
 * `null` when there is no referrer or it is unparseable. `new URL(...).hostname` strips
 * everything but the host, so no path/query/fragment can leak (R73). Mirrors the backend
 * `source` field ("the bare referrer host of the embedding iframe").
 */
function referrerHost() {
    const ref = document.referrer;
    if (ref === "") {
        return null;
    }
    try {
        return new URL(ref).hostname || null;
    }
    catch {
        return null;
    }
}
/**
 * Build the funnel emitter for a form slug. `viewed`/`started` are guarded to fire at most
 * once per `useFunnel` instance (the host calls each from a single mount/first-signal site);
 * `submitted`/`redirectedToPayment` may be called per submit attempt.
 */
export function useFunnel(slug) {
    const token = sessionToken();
    let viewedSent = false;
    let startedSent = false;
    const buildBody = (eventType) => ({
        eventType,
        sessionToken: token,
        locale: activeLocale(),
        source: referrerHost(),
    });
    // Fire-and-forget: recordEvent never throws; deliberately NOT awaited.
    const emit = (eventType) => {
        void recordEvent(slug, buildBody(eventType));
    };
    // Navigation-time emit: `redirectedToPayment` fires right before the top-level OPAY
    // redirect, so it must use sendBeacon to survive the unload (a plain POST would be
    // cancelled). recordEventBeacon never throws and falls back to the axios POST itself.
    const emitBeacon = (eventType) => {
        recordEventBeacon(slug, buildBody(eventType));
    };
    return {
        viewed: () => {
            if (viewedSent) {
                return;
            }
            viewedSent = true;
            emit("Viewed");
        },
        started: () => {
            if (startedSent) {
                return;
            }
            startedSent = true;
            emit("Started");
        },
        submitted: () => emit("Submitted"),
        redirectedToPayment: () => emitBeacon("RedirectedToPayment"),
        sessionToken: token,
    };
}
/**
 * The active document/host locale (BCP-47-style). Read lazily off `<html lang>` set by the
 * host i18n bridge (`setLocale` updates `document.documentElement.lang`), so the funnel
 * reports the locale the donor is actually seeing without coupling `useFunnel` to vue-i18n
 * (keeping it usable from the non-component `useDonationSubmit` flow). Falls back to `"fr"`
 * (the public surface's fallback locale) when unset.
 */
function activeLocale() {
    const lang = document.documentElement.lang;
    return lang !== "" ? lang : "fr";
}
/**
 * Test-only reset of the module-scoped per-visit token, so a spec can assert a fresh visit
 * mints a fresh token without reloading the module. NOT used by production code.
 */
export function __resetFunnelSessionForTest() {
    visitSessionToken = null;
}
